import { ResiliencyState, timeSinceLastResiliencyFailure } from './util';

const storageKey = 'resiliency';
const initialState = {
  failures: {},
  totalFailures: 0
};

const getTimeoutInterval = () => {
  return import.meta?.env?.VITE_CLEAR_FAILURE_INTERVAL_MS
    ? parseInt(import.meta?.env?.VITE_CLEAR_FAILURE_INTERVAL_MS)
    : 30000;
};

const setResiliency = (state: ResiliencyState) => {
  localStorage.setItem(storageKey, JSON.stringify(state));
};

const deleteItemFromFailures = (key: string) => {
  const state = getResiliencyStorage();
  const prevTotalFailures = state.totalFailures;
  const prevFailureCount = state.failures[key]?.count ?? 0;
  const temp = state.failures;
  delete temp[key];

  setResiliency({
    ...state,
    totalFailures: prevTotalFailures - prevFailureCount,
    failures: temp
  });
};

/**
 * Retrieves the resiliency state from local storage.
 *
 * Attempts to get the item associated with `storageKey` from local storage.
 * If the item exists, it parses and returns it as a `ResiliencyState`.
 * If the item does not exist, it returns the default `initialState`.
 *
 * @returns {ResiliencyState} The current resiliency state from local storage or the initial state if not found.
 */
export const getResiliencyStorage = (): ResiliencyState => {
  const item = localStorage.getItem(storageKey);
  return item ? (JSON.parse(item) as ResiliencyState) : initialState;
};

/**
 * Clears all recorded failures by resetting the resiliency state to its initial value.
 *
 * @remarks
 * This function is typically used to remove all failure records from the resiliency storage,
 * restoring it to its default state.
 *
 * @see setResiliency
 * @see initialState
 */
export const clearAllFailures = () => {
  setResiliency(initialState);
};

/**
 * Removes the failure entry associated with the specified key from the resiliency state.
 *
 * @param key - The key identifying the failure to clear.
 */
export const clearFailures = (key: string) => {
  const state = getResiliencyStorage();
  if (state?.failures[key]) {
    deleteItemFromFailures(key);
  }
};

/**
 * Removes expired failure records from the resiliency state.
 *
 * Iterates through all recorded failures and deletes any whose last failure time
 * exceeds the configured circuit breaker interval (`getTimeoutInterval()`).
 *
 * This helps to keep the failure tracking state up-to-date and prevents stale failure
 * records from affecting resiliency logic.
 */
export const clearExpiredFailures = () => {
  const state = getResiliencyStorage();
  for (const key in state?.failures) {
    if (timeSinceLastResiliencyFailure(state.failures[key]) >= getTimeoutInterval()) {
      deleteItemFromFailures(key);
    }
  }
};

/**
 * Increments the failure count for a given key in the resiliency state.
 * If the time since the last failure for the key exceeds the circuit breaker interval,
 * the failure entry is removed before incrementing.
 * Updates the failure count, last failure timestamp, and total failures.
 *
 * @param key - The identifier for the failure entry to increment.
 */
export const incrementFailure = (key: string) => {
  const state = getResiliencyStorage();
  const prev = state.failures[key];

  if (timeSinceLastResiliencyFailure(prev) >= getTimeoutInterval()) {
    deleteItemFromFailures(key);
  }

  setResiliency({
    failures: {
      ...state.failures,
      [key]: {
        count: (prev?.count ?? 0) + 1,
        lastFailureTimeStamp: new Date().toString()
      }
    },
    totalFailures: state.totalFailures + 1
  });
};
